import React, { FC, useMemo, useRef, useState, useEffect } from 'react';
import { IconButton, ArrowIcon } from 'fronton-react';
import { PlayIcon } from '@fronton/icons-react';
import { useSelector } from 'react-redux';
import { getDisplayedName } from 'features/product-info/ducks/selectors';
import { transformVideoThumb } from 'utils/transform-video-thumb';
import { isFirefox, jcm } from 'utils';
import { useTranslation } from 'react-i18next';
import { MediaList } from '../types';
import {
  arrow,
  arrowLeft,
  arrowRight,
  arrowAnimation,
  playIcon,
  playIconWrapper,
  slider,
  sliderContainer,
  video,
  videoContainer,
  videoWrapper,
} from './styles';
import {
  DATA_QA_PREVIEW_VIDEO,
  DATA_QA_PREVIEW_VIDEO_SOURCE,
  DATA_QA_PLAY_ICON_WRAP,
  DATA_QA_PLAY_VIDEO,
  DATA_QA_MEDIA_PREVIEW_VIDEO,
  DATA_QA_VIDEO_ARROW_LEFT,
  DATA_QA_VIDEO_ARROW_RIGHT,
} from '../constants';

type Props = {
  active: number;
  mediaList: MediaList;
  videoList: MediaList;
  setActive: (index: number) => void;
  onMediaLoad?: (mediaType: string, imgSrc: string, loadTime?: number) => void;
};

export const Video: FC<Props> = ({ active, mediaList, videoList, setActive, onMediaLoad }) => {
  const { t } = useTranslation();
  const [isStarted, setIsStarted] = useState<boolean>(false);
  const [isMediaLoaded, setIsMediaLoaded] = useState<boolean>(false);
  const [animation, setAnimation] = useState(false);
  const ref = useRef<HTMLVideoElement[]>([]);
  const sliderRef = useRef<HTMLDivElement>(null);
  const productName = useSelector(getDisplayedName) || '';
  const isFF = useMemo(() => {
    if (typeof navigator !== 'undefined') {
      return isFirefox(navigator?.userAgent);
    }

    return false;
  }, []);
  const current = active - (mediaList?.length - videoList?.length);
  const isArrowLeftVisible = current > 0;
  const isArrowRightVisible = videoList.length && current < videoList.length - 1;

  useEffect(() => {
    if (sliderRef?.current) {
      let transition = current * sliderRef?.current.clientWidth;
      sliderRef.current.style.transform = `translateX(-${transition}px)`;
    }
  }, [current]);

  // react known issue "video element + muted attribute"
  // https://github.com/facebook/react/issues/10389
  useEffect(() => {
    if (ref.current?.length) {
      ref.current.forEach((videoElem) => videoElem.setAttribute('muted', ''));
    }
  }, []);

  useEffect(() => {
    setAnimation(true);
    setTimeout(() => setAnimation(false), 1000);
  }, []);

  function goNext() {
    setActive(active + 1);
  }

  function goPrevious() {
    setActive(active - 1);
  }

  const handlePlayVideo = (src: string) => {
    if (!isMediaLoaded) {
      onMediaLoad?.('mediaVideo', src);
      setIsMediaLoaded(true);
    }
    setIsStarted(true);
  };

  const playVideo = (index: number) => {
    ref.current?.[index]?.play();
    setIsStarted(true);
  };

  const pauseVideo = (index: number) => {
    ref.current?.[index]?.pause();
    setIsStarted(false);
  };

  return (
    <picture data-qa={DATA_QA_MEDIA_PREVIEW_VIDEO} className={videoContainer}>
      {isArrowLeftVisible && (
        <IconButton
          className={jcm({ [arrow]: true, [arrowLeft]: true, [arrowAnimation]: animation })}
          data-qa={DATA_QA_VIDEO_ARROW_LEFT}
          variant="primary"
          label={t('media-gallery.video.prev')}
          size="m"
          onClick={goPrevious}
        >
          <ArrowIcon direction="left" />
        </IconButton>
      )}
      <div className={sliderContainer}>
        <div ref={sliderRef} className={slider}>
          {videoList.map(({ media }, index) => {
            const src = media.url ?? '';
            const thumbSrc = transformVideoThumb(src);

            return (
              <div key={src} className={videoWrapper}>
                {!isStarted && !isFF && (
                  <div
                    data-qa={`${DATA_QA_PLAY_ICON_WRAP}_${index}`}
                    className={playIconWrapper}
                    onClick={() => playVideo(index)}
                  >
                    <PlayIcon
                      className={playIcon}
                      type="fill"
                      color="background-secondary"
                      size="l"
                      data-is-playing={`${isStarted}`}
                      data-qa={DATA_QA_PLAY_VIDEO}
                    />
                  </div>
                )}
                <video
                  key={src}
                  onTouchMove={() => pauseVideo(index)}
                  preload="none"
                  playsInline={true}
                  data-qa={`${DATA_QA_PREVIEW_VIDEO}_${index}`}
                  title={productName}
                  onPlay={() => handlePlayVideo(src)}
                  poster={thumbSrc}
                  ref={(v: HTMLVideoElement) => (ref.current[index] = v)}
                  className={video}
                  controls={true}
                  muted
                  onClick={() => {
                    setIsStarted((prev) => !prev);
                  }}
                >
                  <source
                    src={`${src}`}
                    type="video/mp4"
                    data-qa={`${DATA_QA_PREVIEW_VIDEO_SOURCE}_${index}`}
                  />
                </video>
              </div>
            );
          })}
        </div>
      </div>
      {isArrowRightVisible && (
        <IconButton
          className={jcm({ [arrow]: true, [arrowRight]: true, [arrowAnimation]: animation })}
          data-qa={DATA_QA_VIDEO_ARROW_RIGHT}
          variant="primary"
          label={t('media-gallery.video.next')}
          size="m"
          onClick={goNext}
        >
          <ArrowIcon direction="right" />
        </IconButton>
      )}
    </picture>
  );
};
